<template>
  <list-layout
      no-counts
      no-search
      no-apply-btn
      :title="$t('removal_requests')"
  >
    <template v-slot:beforeFilters>
      <b-row>
        <b-col>
          <filter-builder
              :key="`${userPreferenceNames.TRANSACTION_REMOVAL_REQUESTS_FILTER}-${distributionTabs.selectedAggregation}`"
              :id="`${userPreferenceNames.TRANSACTION_REMOVAL_REQUESTS_FILTER}-${distributionTabs.selectedAggregation}`"
              :filters="filters"
              :apply-filters="applyFilters"
              :reset-filters="resetFilters"
              :filters-pending="filtersPending"
              :filters-applied="filtersApplied"
              @sorted="onSorted"
              @filter="onFilter"
          />
        </b-col>
      </b-row>
      <b-row class="text-center mb-3" align-v="center">
        <b-col>
          <b-form-radio-group
              v-model="distributionTabs.selectedAggregation"
              :options="distributionTabs.aggregationOptions"
              buttons
              button-variant="outline-secondary"
              name="radios-btn-outline"
              class="radio-buttons"
              @change="resetQueryFilters"
          />
        </b-col>
      </b-row>
    </template>

    <template v-slot:table>
      <t-table
          :key="getTableId"
          :columns="columns"
          :options="options"
          :limit="limit"
          ref="removalRequestsList"
          :id="getTableId"
          @loaded="onLoaded"
          @sorted="onSorted"
          @pagination="onPagination"
          @loading="onLoading"
      >
        <template v-slot:counts>
          <div class="d-flex">
            <pagination
                class="smaller p-0"
                :page="page"
                :count="count"
                :loading="loading"
                v-model="limit"
                @limit="onLimit"
            />
          </div>
        </template>

        <template v-slot:merge>
          <b-form-checkbox
            class="ml-3"
            v-model="isRemovalRequest"
            switch
            @change="closeRemovalRequest"
          >
            {{ $t("view_removal_requests") | capitalize }}
          </b-form-checkbox>
        </template>

        <!-- Requester -->
        <template v-slot:createdBy="{ row }">
          {{ row.createdBy?.fullName }}
        </template>
        <!-- Rightsholder ID -->
        <template v-slot:rightsHolderId="{ row }">
          <database-link
              :type="linkTypes.RIGHTS_HOLDER"
              :item-id="row.rightsHolder?.id"
          >
            {{ row.rightsHolder?.resoundId }}
          </database-link>
        </template>
        <!-- Rightsholder Name -->
        <template v-slot:rightsHolderName="{ row }">
          {{ row.rightsHolder?.name }}
        </template>
        <!-- Reviewer -->
        <template v-slot:reviewer="{ row }">
          {{ row.reviewer?.fullName }}
        </template>
        <!-- Execution Date -->
        <template v-slot:executionDate="{ row }">
          {{ row.executionDate }}
        </template>
        <!-- Reason -->
        <template v-slot:reason="{ row }">
          {{ $t(row.reason) }}
        </template>
        <!-- Comments -->
        <template v-slot:comments="{ row }">
          <div v-for="(item, key) in row.comments" :key="key" class="RemovalRequest__comment d-flex">
            <div>
              <fa-icon size="lg" :icon="['far','comment-lines']"/> 
            </div>
            <div class="pl-3">
              <span class="font-weight-bold">
                {{ parseDate(item.created, $config.DATE_FORMAT) }} {{ parseDate(item.created, $config.TIME_FORMAT) }}
              </span>
              -
              {{ 
              `${item.createdBy.firstName}
              ${item.createdBy.lastName}
              (${item.createdBy.memberCollective ? item.createdBy.memberCollective : item.createdBy.daAgent})
              ${$t("wrote")}:` 
              }}
              <div>
                {{ item.comment }}
              </div>
            </div>
          </div>
        </template>
        <!-- Recording ID -->
        <template v-slot:recordingId="{ row }">
          <database-link
              :type="linkTypes.RECORDING"
              :item-id="row.recording?.id"
          >
            {{ row.recording?.resoundId }}
          </database-link>
        </template>
        <!-- Artist -->
        <template v-slot:recordingArtist="{ row }">
          {{ row.recording?.artist }}
        </template>
        <!-- Title -->
        <template v-slot:recordingTitle="{ row }">
          {{ row.recording?.title }}
        </template>
        <!-- Type -->
        <template v-slot:type="{ row }">
          {{ capitalize(row.rightsHolder?.type) }}
        </template>

        <!-- Status -->
        <template v-slot:status="{ row }">
          <div v-if="row.status === transactionRemovalStatuses.APPROVED">
            <status
                v-if="row.executionStatus === payoutJobExecutionStatuses.PENDING"
                :text="capitalize($t('removal_queued'))"
                name="removal_queued"
                :icon="['fas', 'question-circle']"
                color="orange"
            />
            <status
                v-if="row.executionStatus === payoutJobExecutionStatuses.IN_PROGRESS"
                :text="capitalize($t('removal_progress'))"
                name="removal_progress"
                :icon="['fas', 'question-circle']"
                color="orange"
            />
            <status
                v-if="row.executionStatus === payoutJobExecutionStatuses.COMPLETED"
                :text="capitalize($t('removed'))"
                name="removed"
                :icon="['fas', 'check-circle']"
                color="green"
            />
            <status
                v-if="row.executionStatus === payoutJobExecutionStatuses.ERROR"
                name="removal_denied"
                :text="capitalize($t('removal_denied'))"
                :icon="['fas', 'exclamation-circle']"
                color="red"
            />
          </div>
          <div v-else>
            <status
                v-if="row.status === transactionRemovalStatuses.IN_PROGRESS || row.status === transactionRemovalStatuses.PENDING"
                :text="capitalize($t('removal_requested'))"
                name="removal_requested"
                :icon="['fas', 'question-circle']"
                color="orange"
            />
            <status
                v-if="row.status === transactionRemovalStatuses.REJECTED"
                name="removal_denied"
                :text="capitalize($t('removal_denied'))"
                :icon="['fas', 'exclamation-circle']"
                color="red"
            />
          </div>
        </template>
      </t-table>
    </template>
  </list-layout>
</template>

<script>
import { capitalize, get, snakeCase, upperCase } from "lodash"
import {
  filterTypes,
  linkTypes,
  payoutJobExecutionStatuses,
  pendingQueueAggregations,
  perPageItemsLg,
  rightsholderTypes,
  transactionRemovalFullFilter,
  transactionRemovalStatuses,
  userPreferenceNames,
} from "@/constants"
import { mapActions, mapState } from "vuex"
import DatabaseLink from "@/components/DatabaseLink.vue"
import FilterBuilder from "@/components/FilterBuilder"
import { Pages } from "@/utils/pages"
import Pagination from "@/components/Pagination"
import { listRouteMixin } from "@/utils/mixins"
import parseDate from "@/utils/date-parser"

export default {
  name: "PendingQueueSection",
  mixins: [listRouteMixin],
  components: { FilterBuilder, Pagination, DatabaseLink },
  props: {
    payoutId: String,
  },
  data () {
    return {
      page: 1,
      count: 0,
      limit: perPageItemsLg,
      loading: false,
      userPreferenceNames,
      capitalize,
      Pages,
      payoutJobExecutionStatuses,
      transactionRemovalStatuses,
      isRemovalRequest: true,
      linkTypes,
      distributionTabs: {
        aggregationOptions: [
          {
            value: "rightsHolder",
            text: this.$t("rights_holder_title"),
          },
          {
            value: "recordingRightsHolder",
            text: this.$t("rights_holder_recording"),
          },
          {
            value: "rightsHolderTariffRecording",
            text: this.$t("rights_holder_tariff_recording"),
          },
        ],
        selectedAggregation: "rightsHolder",
      },
      filters: {
        id: { value: "", defaultValue: "", type: filterTypes.TEXT, label: upperCase(this.$t("id")), hidden: true },
        description: {
          value: "",
          defaultValue: "",
          type: filterTypes.TEXT,
          label: capitalize(this.$t("tariff_period")),
          hidden: false
        },
        recordingArtist: {
          value: "",
          defaultValue: "",
          type: filterTypes.TEXT,
          label: capitalize(this.$t("artist")),
          hidden: false
        },
        collectiveSociety: {
          value: "",
          defaultValue: "",
          type: filterTypes.TEXT,
          label: capitalize(this.$t("collective_society")),
          hidden: false
        },
        amount: {
          value: null,
          defaultValue: null,
          type: filterTypes.NUMBER_RANGE_NO_SLIDER,
          label: capitalize(this.$t("net_amount")),
          hidden: false
        },
        recording: {
          value: "",
          defaultValue: "",
          type: filterTypes.TEXT,
          label: capitalize(this.$t("recording")),
          hidden: true
        },
        rightsholder: {
          value: "",
          defaultValue: "",
          type: filterTypes.TEXT,
          label: capitalize(this.$t("rightsholder")),
          hidden: true
        },
        payee: { value: "", defaultValue: "", type: filterTypes.TEXT, label: capitalize(this.$t("payee")), hidden: true },
        removal_status: {
          value: "",
          defaultValue: "",
          type: filterTypes.SELECT,
          options: [
            {
              text: capitalize(this.$t("removal_requested")),
              value: transactionRemovalFullFilter.REQUESTED
            },
            {
              text: capitalize(this.$t("removal_queued")),
              value: transactionRemovalFullFilter.PENDING
            },
            {
              text: capitalize(this.$t("removal_progress")),
              value: transactionRemovalFullFilter.IN_PROGRESS
            },
            {
              text: capitalize(this.$t("removed")),
              value: transactionRemovalFullFilter.COMPLETED
            },
            {
              text: capitalize(this.$t("removal_denied")),
              value: transactionRemovalFullFilter.ERROR
            },
          ],
          label: capitalize(this.$t("status")),
          hidden: true
        },
        reviewer: {
          value: null,
          defaultValue: null,
          type: filterTypes.SELECT_SEARCH,
          listEndpoint: this.$api.users.usersSearch,
          excludeName: "id",
          textField: "name",
          valueField: "email",
          label: capitalize(this.$t("reviewer")),
          placeholder: capitalize(this.$t("search_user")),
          searchName: "search",
        },
        requester: {
          value: null,
          defaultValue: null,
          type: filterTypes.SELECT_SEARCH,
          listEndpoint: this.$api.users.usersSearch,
          excludeName: "id",
          textField: "name",
          valueField: "email",
          label: capitalize(this.$t("requester")),
          placeholder: capitalize(this.$t("search_user")),
          searchName: "search",
        },
        type: {
          value: null,
          defaultValue: null,
          label: capitalize(this.$t("type")),
          options: this.rightsHolderType(),
          type: filterTypes.SELECT,
          hidden: false
        },
        requestDate: {
          value: null,
          defaultValue: null,
          type: filterTypes.DATE_RANGE,
          format: "YYYY-MM-DD",
          label: capitalize(this.$t("request_date")),
          noWatch: true,
          hidden: false
        },
        executionDate: {
          value: null,
          defaultValue: null,
          type: filterTypes.DATE_RANGE,
          format: "YYYY-MM-DD",
          label: capitalize(this.$t("execution_date")),
          noWatch: true,
          hidden: false
        },
        rightsHolder: {
          value: null,
          defaultValue: null,
          type: filterTypes.SELECT_SEARCH,
          label: capitalize(this.$t("rights_holder")),
          placeholder: capitalize(this.$t("search_rightsholder")),
          excludeName: "id",
          valueField: "resoundId",
          textField: "name",
          searchName: "search",
          listEndpoint: this.$api.rightsholders.rightsholdersList,
          noWatch: true,
        },
      },
      options: {
        sortable: [
          "createdBy",
          "createdAt",
          "rightsHolderId",
          "rightsHolderName",
          "payeeName",
          "societyName",
          "amount",
          "transactionsCount",
          "status",
          "reviewer",
          "executionDate",
          "distributionDescription",
          "recordingId",
          "recordingArtist",
          "recordingTitle",
          "type",
          "playcount",
          "share",
          "reason",
          "comments",
        ],
        headings: {
          createdBy: capitalize(this.$t("requester")),
          createdAt: capitalize(this.$t("request_date")),
          rightsHolderId: capitalize(this.$t("rights_holder_id")),
          rightsHolderName: capitalize(this.$t("rights_holder_name")),
          payeeName: capitalize(this.$t("payee_name")),
          societyName: capitalize(this.$t("collective_society")),
          amount: capitalize(this.$t("net_amount")),
          transactionsCount: this.$t("transactions_count"),
          status: capitalize(this.$t("status")),
          reviewer: capitalize(this.$t("reviewer")),
          executionDate: capitalize(this.$t("execution_date")),
          distributionDescription: capitalize(this.$t("tariff_periods")),
          recordingId: this.$t("recording_id"),
          recordingArtist: capitalize(this.$t("artist")),
          recordingTitle: capitalize(this.$t("title")),
          type: capitalize(this.$t("type")),
          playcount: capitalize(this.$t("total_playcount")),
          share: capitalize(this.$t("share")),
          reason: capitalize(this.$t("reason")),
          comments: capitalize(this.$t("comments")),
        },
        width: {
          createdBy: 150,
          createdAt: 150,
          status: 150,
          action: 150,
          comments: 150,
        },
        responseAdapter ({ data }) {
          return {
            data: data.results,
            count: data.count,
          }
        },
        requestFunction (queryParams) {
          queryParams = { ...queryParams, ...this.$route.query }
          this.page = Number(get(queryParams, "page", 1))
          queryParams.aggregation_level = pendingQueueAggregations[queryParams.aggregation_level] || pendingQueueAggregations.rightsHolder
          queryParams.payout_id = this.$parent.$parent.payoutId
          return this.$api.payouts.getTransactionsRemovalList(queryParams)
        },
      }
    }
  },
  mounted () {
    this.distributionTabs.selectedAggregation = this.$route.query["aggregation_level"] || "rightsHolder"
    this.filtersWithOptions()
    this.resetQueryFilters()
  },
  computed: {
    ...mapState("user", ["permissions"]),
    getTableId () {
      return `removal-requests-${this.distributionTabs.selectedAggregation}`
    },
    columns () {
      let columnsSet
      switch (this.distributionTabs.selectedAggregation) {
      case "rightsHolder":
        columnsSet = [
          "createdBy",
          "createdAt",
          "rightsHolderId",
          "rightsHolderName",
          "payeeName",
          "societyName",
          "amount",
          "transactionsCount",
          "status",
          "executionDate",
          "action",
          "reviewer",
          "reason",
          "comments",
        ]
        break
      case "rightsHolderTariffRecording":
        columnsSet = [
          "createdBy",
          "createdAt",
          "distributionDescription",
          "recordingId",
          "recordingArtist",
          "recordingTitle",
          "type",
          "rightsHolderId",
          "rightsHolderName",
          "payeeName",
          "societyName",
          "playcount",
          "share",
          "amount",
          "status",
          "executionDate",
          "action",
          "reviewer",
          "reason",
          "comments",
        ]
        break
      default:
        columnsSet = [
          "createdBy",
          "createdAt",
          "rightsHolderId",
          "rightsHolderName",
          "payeeName",
          "societyName",
          "amount",
          "transactionsCount",
          "status",
          "executionDate",
          "comments",
          "action",
          "reviewer",
          "reason",
          "comments",
        ]
      }
      columnsSet.push(...["action", "reviewer", "reason", "comments"])
      return columnsSet
    },
  },
  methods: {
    ...mapActions("alert", ["success", "error"]),
    parseDate,
    onLoaded ({ data }) {
      this.count = data?.count || 0
      this.summary = data?.summary || {}
      this.loading = false
    },
    getData () {
      this.$refs.removalRequestsList.getData()
    },
    onFilter (name, value){
      if (name === "requestDate") name = "createdAt"
      if (["createdAt", "executionDate"].includes(name)) {
        if (value !== null && value.length) {
          const range = (value) ?
            [
              this.parseDate(value[0], this.$config.ISO_DATE_FORMAT),
              this.parseDate(value[1], this.$config.ISO_DATE_FORMAT)
            ] : []
          this.changeFilter(`${name}_min`, range[0])
          this.changeFilter(`${name}_max`, range[1])
        } else {
          delete this.queryParams[snakeCase(name) +  "_min"]
          delete this.queryParams[snakeCase(name) +  "_max"]
        }
        
      }
      else if (name === "requester" || name === "reviewer") {
        if (value === null) {
          this.changeFilter(name, value)
        } else {
          this.changeFilter(name, value.email || value.value)
        }
      }
      else if (name === "rightsHolder") {
        if (value === null) {
          this.changeFilter(name, value)
        } else {
          this.changeFilter(name, value.resoundId || value.value)
        }
      }
    },
    rightsHolderType (){
      return [
        {
          text: capitalize(rightsholderTypes.PERFORMER),
          value: rightsholderTypes.PERFORMER
        },
        {
          text: capitalize(rightsholderTypes.MAKER),
          value: rightsholderTypes.MAKER
        }
      ]
    },
    filtersWithOptions () {
      let selectedFilters = []
      switch (this.distributionTabs.selectedAggregation) {
      case "rightsHolder":
        selectedFilters = [
          "id",
          "description",
          "amount",
          "collectiveSociety",
          "rightsHolder",
          "payee",
          "removal_status",
          "reviewer",
          "requester",
          "requestDate",
          "executionDate",
        ]
        break
      case "rightsHolderTariffRecording":
        selectedFilters = [
          "id",
          "description",
          "amount",
          "collectiveSociety",
          "rightsHolder",
          "payee",
          "removal_status",
          "reviewer",
          "requester",
          "requestDate",
          "executionDate",
          "recording",
          "recordingArtist",
          "title",
          "type",
        ]
        break
      default:
        selectedFilters = ["id", "amount", "description"]
      }
      this.filters = Object.fromEntries(
        Object.entries(this.filters).map(([key, filter]) => [
          key,
          { ...filter, hidden: !selectedFilters.includes(key) }
        ])
      )
    },
    resetQueryFilters () {
      this.resetFilters(true)
    },
    closeRemovalRequest () {
      this.$emit("closeRemovalRequest")
    }
  },
  watch: {
    "distributionTabs.selectedAggregation": function (value) {
      this.queryParams = {
        ...this.queryParams, "aggregation_level": value
      }
      this.changeFilter("aggregationLevel", value, true)
      this.filtersWithOptions()
    }
  },
}
</script>

<style lang="scss" scoped>
.RemovalRequest__comment:not(:last-child) {
  padding-bottom: 1rem;
}
</style>